/**
 * Results breakdown screen. Shows headline cost with a per-interaction / per-day /
 * per-month / per-year period switch, token cards, cost breakdown, detailed trace.
 */

import type { Currency, EstimateResult, ModelInfo } from '@tcc/shared';
import { DEFAULT_DAYS_PER_MONTH, DEFAULT_DAYS_PER_YEAR } from '@tcc/shared';
import { useState } from 'react';

const CURRENCY_LOCALE: Record<Currency, string> = { GBP: 'en-GB', USD: 'en-US' };

const moneyFmt = (currency: Currency, dp: number) =>
	new Intl.NumberFormat(CURRENCY_LOCALE[currency], {
		style: 'currency',
		currency,
		minimumFractionDigits: dp,
		maximumFractionDigits: dp,
	});
const int = new Intl.NumberFormat('en-GB', { maximumFractionDigits: 0 });

type Period = 'interaction' | 'day' | 'month' | 'year';

const PERIOD_LABELS: Record<Period, string> = {
	interaction: 'Per interaction',
	day: 'Per day',
	month: 'Per month',
	year: 'Per year',
};

const INPUT_LABELS: Record<string, string> = {
	text: 'Text',
	images: 'Images',
	systemPrompt: 'System prompt',
	ragContext: 'RAG context',
	additionalContext: 'Additional context',
	video: 'Video',
	voice: 'Voice',
};
const OUTPUT_LABELS: Record<string, string> = {
	chainOfThought: 'Chain of thought (monthly flat)',
	rationale: 'Base response (per response)',
	decisionParagraphs: 'Decision paragraphs (monthly flat)',
	thinking: 'Extended thinking',
};

export interface ResultsScreenProps {
	result: EstimateResult;
	models: ModelInfo[];
	onAdjust: () => void;
	onRestart: () => void;
	onModelChange: (modelId: string) => void;
	/** When true, render without the outer .sg/.sc card wrapper (embedded in the clean wizard grid). */
	embedded?: boolean;
	/** Days-per-month basis for per-day figures (default 365/12). */
	daysPerMonth?: number;
	/** Days-per-year basis for per-year figures (default 365). */
	daysPerYear?: number;
}

export function ResultsScreen({
	result,
	models,
	onAdjust,
	onRestart,
	onModelChange,
	embedded,
	daysPerMonth = DEFAULT_DAYS_PER_MONTH,
	daysPerYear = DEFAULT_DAYS_PER_YEAR,
}: ResultsScreenProps) {
	const [exportError, setExportError] = useState<string | null>(null);
	const [showDetail, setShowDetail] = useState(false);
	const [period, setPeriod] = useState<Period>('month');

	// Currency-aware money formatters (driven by the computed result currency).
	const money0 = moneyFmt(result.currency, 0);
	const money2 = moneyFmt(result.currency, 2);
	const money4 = moneyFmt(result.currency, 4);

	// Derive cost/token values for each period.
	const n = result.interactionsPerMonth;
	const monthlyCost = result.estimatedMonthlyCost;
	// True per-interaction cost (divide full-precision monthly by volume).
	const perInteractionCost = n > 0 ? monthlyCost / n : 0;
	// Volumetrics basis: monthly = daily × daysPerMonth, yearly = daily × daysPerYear,
	// so day/year figures scale from the monthly total using the same basis.
	const dpm = daysPerMonth > 0 ? daysPerMonth : DEFAULT_DAYS_PER_MONTH;
	const dpy = daysPerYear > 0 ? daysPerYear : DEFAULT_DAYS_PER_YEAR;
	const yearFactor = dpy / dpm;
	const perDayCost = monthlyCost / dpm;
	const perYearCost = monthlyCost * yearFactor;

	// Per-interaction input tokens: inputTokens.total is already per-turn from the engine.
	const inPerTurn = result.inputTokens.total;
	// Per-turn output: outputTokens.total includes the base response plus per-run
	// decision overhead (chainOfThought + decisionParagraphs), all per turn.
	const outPerTurn = result.outputTokens.total;

	function scaledCost(p: Period): number {
		switch (p) {
			case 'interaction':
				return perInteractionCost;
			case 'day':
				return perDayCost;
			case 'month':
				return monthlyCost;
			case 'year':
				return perYearCost;
		}
	}
	function scaledInteractions(p: Period): number {
		switch (p) {
			case 'interaction':
				return 1;
			case 'day':
				return n / dpm;
			case 'month':
				return n;
			case 'year':
				return n * yearFactor;
		}
	}
	function fmtCost(c: number): string {
		if (Math.abs(c) >= 10) return money0.format(c);
		if (Math.abs(c) >= 0.01) return money2.format(c);
		return money4.format(c);
	}
	function fmtLabel(p: Period): string {
		if (p === 'interaction') return 'Est. cost per interaction';
		if (p === 'day') return 'Est. cost per day';
		if (p === 'month') return 'Est. monthly cost';
		return 'Est. annual cost';
	}

	function exportResult() {
		try {
			const blob = new Blob([JSON.stringify(result, null, 2)], { type: 'application/json' });
			const url = URL.createObjectURL(blob);
			const a = document.createElement('a');
			a.href = url;
			a.download = `token-cost-estimate-${result.model.modelId}.json`;
			document.body.appendChild(a);
			a.click();
			a.remove();
			URL.revokeObjectURL(url);
			setExportError(null);
		} catch {
			setExportError('Export could not be completed.');
		}
	}

	const inputEntries = Object.entries(result.inputTokens.breakdown).filter(([, v]) => typeof v === 'number') as [
		string,
		number,
	][];
	const outputEntries = Object.entries(result.outputTokens.breakdown).filter(([, v]) => typeof v === 'number') as [
		string,
		number,
	][];

	return (
		<div className={embedded ? undefined : 'sg'}>
			<div className={embedded ? undefined : 'sc'}>
				<div className="tbar">
					<span className="wm">Lloyds Banking Group</span>
				</div>

				{/* Period switcher */}
				{/* biome-ignore lint/a11y/useSemanticElements: toggle buttons grouped by role="group" — <fieldset> is inappropriate here as these are not form inputs */}
				<div role="group" aria-label="Cost period" className="period-group">
					{(['interaction', 'day', 'month', 'year'] as Period[]).map((p) => (
						<button
							key={p}
							type="button"
							aria-pressed={period === p}
							onClick={() => setPeriod(p)}
							className="period-btn"
						>
							{PERIOD_LABELS[p]}
						</button>
					))}
				</div>

				{/* Hero cost */}
				<div className="rhero">
					<div className="rlbl">{fmtLabel(period)}</div>
					<div className="rnum">{fmtCost(scaledCost(period))}</div>
					<div className="rsub">
						{int.format(Math.round(scaledInteractions(period)))} interactions
						{period !== 'interaction' ? ` / ${period}` : ''} &middot; {result.model.displayName}
					</div>
				</div>

				{/* Model card */}
				<div className="mcard">
					<div>
						<div className="mlbl">Model (LLM)</div>
						<div className="mname">{result.model.displayName}</div>
						<div className="mprice">
							${result.model.inputPricePerMillion} / 1M in &middot; ${result.model.outputPricePerMillion} / 1M out
						</div>
					</div>
					<div className="mchg">
						<label htmlFor="model-select" className="sr-only">
							Model
						</label>
						<select id="model-select" value={result.model.modelId} onChange={(e) => onModelChange(e.target.value)}>
							{models.map((m) => (
								<option key={m.modelId} value={m.modelId}>
									{m.displayName}
								</option>
							))}
						</select>
					</div>
				</div>

				{/* Token cards — per-turn figures */}
				<div className="tkgrid">
					<div className="tcard">
						<div className="tclbl">Input tokens / turn</div>
						<div className="tcval">~{int.format(inPerTurn)}</div>
						<div className="tcdtl">
							{inputEntries.map(([k, v]) => (
								<span key={k}>
									{INPUT_LABELS[k] ?? k}: {int.format(v)}
									<br />
								</span>
							))}
						</div>
					</div>
					<div className="tcard">
						<div className="tclbl">Output tokens / turn</div>
						<div className="tcval">~{int.format(outPerTurn)}</div>
						<div className="tcdtl">
							{outputEntries.map(([k, v]) => (
								<span key={k}>
									{OUTPUT_LABELS[k] ?? k}: {int.format(v)}
									<br />
								</span>
							))}
							{(result.outputTokens.breakdown.chainOfThought ?? 0) > 0 && (
								<span className="tcard-footnote">† monthly flat (not per-turn)</span>
							)}
						</div>
					</div>
				</div>

				{/* Per-period stats row */}
				<div className="perrow">
					<div className="pcrd">
						<div className="plbl">Cost / interaction</div>
						<div className="pval">{money4.format(perInteractionCost)}</div>
					</div>
					<div className="pcrd">
						<div className="plbl">Tokens / turn</div>
						<div className="pval">~{int.format(inPerTurn + outPerTurn)}</div>
					</div>
					{typeof result.modelCallsPerTurn === 'number' && (
						<div className="pcrd">
							<div className="plbl">Model calls / turn</div>
							<div className="pval">{int.format(result.modelCallsPerTurn)}</div>
						</div>
					)}
					{typeof result.requestsPerMonth === 'number' && result.requestsPerMonth !== n && (
						<div className="pcrd">
							<div className="plbl">Requests / month</div>
							<div className="pval">{int.format(result.requestsPerMonth)}</div>
						</div>
					)}
				</div>

				{/* Cost breakdown table scaled to selected period */}
				<div className="bkdn">
					{/* Tokens — always show monthly equivalents */}
					<div className="brow">
						<span className="blbl">Input tokens / month</span>
						<span className="bval">{int.format(inPerTurn * n)}</span>
					</div>
					<div className="brow">
						<span className="blbl">Output tokens / month</span>
						<span className="bval">{int.format(outPerTurn * n)}</span>
					</div>
					<div className="brow">
						<span className="blbl">Interactions / {period}</span>
						<span className="bval">{int.format(Math.round(scaledInteractions(period)))}</span>
					</div>
					<div className="brow tot">
						<span className="blbl">{fmtLabel(period)}</span>
						<span className="bval">{fmtCost(scaledCost(period))}</span>
					</div>
				</div>

				{exportError && (
					<div className="alert" role="alert">
						{exportError}
					</div>
				)}

				{/* Detailed calculation trace */}
				{result.calculation && result.calculation.length > 0 && (
					<>
						<button type="button" className="btns" onClick={() => setShowDetail((s) => !s)}>
							{showDetail ? 'Hide' : 'Show'} detailed estimate
						</button>
						{showDetail && (
							<div className="bkdn trace-bkdn">
								{(['input', 'output', 'cost'] as const).map((sec) => (
									<div key={sec}>
										<div className="brow tot">
											<span className="blbl trace-section">{sec} calculation</span>
											<span className="bval" />
										</div>
										{result.calculation
											?.filter((l) => l.section === sec)
											.map((l) => (
												<div className="brow trace-row" key={l.label}>
													<span className="blbl trace-label">{l.label}</span>
													<span className="bval trace-value">{l.formula}</span>
												</div>
											))}
									</div>
								))}
							</div>
						)}
					</>
				)}

				<button type="button" className="btnp" onClick={exportResult}>
					Export estimate
				</button>
				<button type="button" className="btns" onClick={onAdjust}>
					Adjust inputs
				</button>
				<button type="button" className="btns btn-restart" onClick={onRestart}>
					Start over
				</button>
			</div>
		</div>
	);
}
